import axios, { AxiosResponse } from 'axios';
import { SecurityRoles } from '../common/utils';

export const defaultUser: UserDetails = {
	firstName: '',
	lastName: '',
	email: '',
	mailingAddress: '',
	mailingCity: '',
	mailingProvince: '',
	mailingPostalCode: '',
	mailingCountry: '',
	isSameAddress: true,
	billingAddress: '',
	billingCity: '',
	billingProvince: '',
	billingPostalCode: '',
	billingCountry: '',
	role: [SecurityRoles.Customer],
	gender: '',
};

export const defaultCustomerNote: CustomerNote = {
	notes: '',
	answerId: undefined
};

const apiUrl = process.env.REACT_APP_API_URL;
const userAPI = {
	fetchUser: (userId: string) => {
		return axios.get(`${apiUrl}/users/${userId}/details.json`)
			.then((resp: AxiosResponse) => resp.data);
	},
	fetchUserByPolicyId: (policyId: string) => {
		return axios.get(`${apiUrl}/policies/${policyId}/user.json`)
			.then((resp: AxiosResponse) => resp.data);
	},
	fetchReferrerNameByReferralCode: (referralCode: string) => {
		return axios.get(`${apiUrl}/referralCode/${referralCode}/name.json`)
			.then((resp: AxiosResponse) => resp.data);
	},
	fetchReferrerNameBySpousalReferralCode: (spousalReferralCode: string) => {
		return axios.get(`${apiUrl}/spousalReferralCode/${spousalReferralCode}/name.json`)
			.then((resp: AxiosResponse) => resp.data);
	},
	saveUser: (user: UserDetails) => {
		return axios.post(`${apiUrl}/users/details`, user)
			.then((resp: AxiosResponse) => resp.data);
	},
	adminSaveUser: (user: UserDetails) => {
		return axios.post(`${apiUrl}/admin/users/details.json`, user)
			.then((resp: AxiosResponse) => resp.data);
	},
	updateSoftDeclinedUser: (user: UserDetails, policyId: string) => {
		const data = {
			user,
			policyId
		};
		return axios.post(`${apiUrl}/users/soft-declined-details`, data)
			.then((resp: AxiosResponse) => resp.data);
	},
	saveStaff: (user: UserDetails) => {
		return axios.post(`${apiUrl}/users/admins`, user)
			.then((resp: AxiosResponse) => resp.data);
	},
	fetchAdmins: () => {
		return axios.get(`${apiUrl}/users/admins.json`)
			.then((resp: AxiosResponse) => resp.data);
	},
	fetchCustomers: (filterProspects?: boolean) => {
		const options = {
			params: { filterProspects: filterProspects || null }
		};

		return axios.get(`${apiUrl}/users/customers.json`, options)
			.then((resp: AxiosResponse) => resp.data);
	},
	fetchSoftDeclinedCustomers: () => {
		return axios.get(`${apiUrl}/users/soft-declined-customers.json`)
			.then((resp: AxiosResponse) => resp.data);
	},
	clearAdjudicationQueue: () => {
		return axios.post(`${apiUrl}/users/clear-adjudication-queue`)
			.then((resp: AxiosResponse) => resp.data);
	},
	fetchCustomerNote: (customerId: string, noteId: string) => {
		return axios.get(`${apiUrl}/users/customers/${customerId}/notes/${noteId}.json`)
			.then((resp: AxiosResponse) => resp.data);
	},
	fetchCustomerNotes: (customerId: string) => {
		return axios.get(`${apiUrl}/users/customers/${customerId}/notes.json`)
			.then((resp: AxiosResponse) => resp.data);
	},
	saveCustomerNote: (note: CustomerNote) => {
		return axios.post(`${apiUrl}/users/customers/notes`, note)
			.then((resp: AxiosResponse) => resp.data);
	},
	resetPassword: (userId: string, resetUrl: string) => {
		const data = { userId, resetUrl };

		return axios.post(`${apiUrl}/emails/reset-password`, data)
			.then((resp: AxiosResponse) => resp.data);
	},
	saveRole: (userId: string, roles: string[]) => {
		const data = { roles };

		return axios.post(`${apiUrl}/users/${userId}/update-role`, data)
			.then((resp) => resp.data);
	},

	fetchReferrals: (customerId: string) => {
		return axios.get(`${apiUrl}/users/customers/${customerId}/referrals.json`)
			.then((resp: AxiosResponse) => resp.data);
	},
	generateNewReferralCode: (customerId: string) => {
		return axios.post(`${apiUrl}/users/customers/${customerId}/referrals/GenerateNewReferralCode`)
			.then((resp: AxiosResponse) => resp.data);
	},
	updateReferralBalance: (customerId: string, referralBalance: number) => {
		return axios.post(`${apiUrl}/users/customers/${customerId}/referralBalance`, { referralBalance: referralBalance })
			.then((resp: AxiosResponse) => resp.data);
	},
	updateRemainingReferrals: (customerId: string, remainingReferrals: number) => {
		return axios.post(`${apiUrl}/users/customers/${customerId}/referrals`, { remainingReferrals: remainingReferrals })
			.then((resp: AxiosResponse) => resp.data);
	},
	updateCustomerReferralEnabled: (customerId: string) => {
		return axios.post(`${apiUrl}/users/customers/${customerId}/referralEnabled`)
			.then((resp: AxiosResponse) => resp.data);
	},
	sendReferralInviteEmail: (userId: string, name: string, email: string) => {
		const data = { userId, name, email };
		return axios.post(`${apiUrl}/emails/referral-invite`, data)
			.then((resp: AxiosResponse) => resp.data);
	},
	sendSpousalReferralInviteEmail: (userId: string, name: string, email: string) => {
		const data = { userId, name, email };
		return axios.post(`${apiUrl}/emails/spousal-referral-invite`, data)
			.then((resp: AxiosResponse) => resp.data);
	},
	sendSoftDeclinedEmail: (userId: string) => {
		const data = { userId };
		return axios.post(`${apiUrl}/emails/soft-declined`, data)
			.then((resp: AxiosResponse) => resp.data);
	},
	updateStripeCustomerEmail: (customerId: string, email: string) => {
		const data = {
			customerId: customerId,
			email: email
		};

		return axios.post(`${apiUrl}/customers/email/update`, data)
			.then((resp: AxiosResponse) => resp.data);
	},
	updateUserWithEmailChecker: (user: UserDetails) =>  {
		return axios.post(`${apiUrl}/users/details-email-check.json`, user)
			.then((resp: AxiosResponse) => resp.data );
	},
};

export default userAPI;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/user/userAPI.ts